import React from "react";
import { cn } from "src/common/utils";

import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./dialog";

interface UIConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  children?: React.ReactNode;
  confirmText?: React.ReactNode;
  cancelText?: string;
  variant?: "destructive" | "default";
  isLoading?: boolean;
  showCancel?: boolean;
  confirmDisabled?: boolean;
  contentClassName?: string;
}

export function UIConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  isLoading = false,
  showCancel = true,
  confirmDisabled = false,
  contentClassName,
}: UIConfirmationDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn("w-[calc(100vw-2rem)] sm:max-w-md bg-popover text-popover-foreground border-border", contentClassName)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-muted-foreground">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {children && (
          <div className="text-muted-foreground max-h-[calc(100vh-250px)] overflow-y-auto custom-scrollbar">
            {children}
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          {showCancel && (
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant={variant}
            onClick={handleConfirm}
            disabled={isLoading || confirmDisabled}
            className={`${
              variant === "destructive"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : ""
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}